import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { isApiError } from '../lib/apiClient';
import './AuthLayout.css';

type Step = 'request' | 'reset';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.forgotPassword(email.trim().toLowerCase());
      setNotice(res.message);
      setStep('reset');
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Could not send a code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.resetPassword(email.trim().toLowerCase(), code.trim(), newPassword);
      navigate('/login', { replace: true });
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Could not reset the password. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__card card">
        <div className="auth__brand">
          <span className="auth__brand-mark">✦</span> FASD Assist
        </div>
        <h1>{step === 'request' ? 'Reset password' : 'Enter your code'}</h1>
        <p className="muted auth__sub">
          {step === 'request'
            ? 'We’ll email you a 6-digit code.'
            : 'Check your email for the 6-digit code, then set a new password.'}
        </p>

        {step === 'request' ? (
          <form onSubmit={requestCode}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {error && <p className="error-text">{error}</p>}
            <button className="btn btn--block" type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={doReset}>
            {notice && <p className="muted" style={{ fontSize: 13 }}>{notice}</p>}
            <div className="field">
              <label htmlFor="code">6-digit code</label>
              <input
                id="code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="error-text">{error}</p>}
            <button className="btn btn--block" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Set new password'}
            </button>
          </form>
        )}

        <div className="auth__foot">
          <Link to="/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
